import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Check, Package, Home, Loader2, Code, ChevronDown, ChevronUp, AlertCircle, Mail } from 'lucide-react';
import { getUserOrders, OrderData } from '../lib/stripe';
import { supabase } from '../lib/supabase';
import { getProductByPriceId } from '../stripe-config';
import { useFrame } from '../contexts/FrameContext';
import { getAdminModeHeaders } from '../utils/adminMode';
import { campaignTracking } from '../services/campaignTracking';
import { 
  trackPurchaseCompleted, 
  trackOrderConfirmationView, 
  trackStripeCheckoutView, 
  trackFunnelStep,
  trackConfiguratorView,
  trackRedditPurchase,
  trackFacebookPurchase,
  trackPinterestPurchase
} from '../utils/analytics';
import { updateWidgetForSuccess } from '../utils/widgetSync';

interface FrameConfig {
  selectedFrame: string;
  selectedSize: string;
  selectedStyle: string;
  imageUrl: string;
  frameUrl: string;
  frameName: string;
  lumaprintsOrderId?: string;
  sizeDetails: {
    id: string;
    name: string;
    width: number;
    height: number;
    frameWidth?: number;
    frameHeight?: number;
    price: number;
    description: string;
  };
  timestamp: number;
}

interface DebugState {
  sessionId: string | null;
  frameConfig: FrameConfig | null;
  order: OrderData | null;
  orderSubmitted: boolean;
  orderSubmitting: boolean;
  dependencyCheck: {
    sessionId: boolean;
    frameConfig: boolean;
    order: boolean;
    orderSubmitted: boolean;
    orderSubmitting: boolean;
  };
  lastCheck: string;
}

// Enhanced function to extract frame size from optimized image URL
function extractFrameSizeFromImageUrl(imageUrl: string): string | null {
  // Look for patterns like "optimized-XS-", "optimized-S-", "optimized-M-", "optimized-L-"
  // CRITICAL: Check XS first before S to avoid matching the S in XS
  const match = imageUrl.match(/optimized-(XS|S|M|L)-/);
  if (match) {
    return match[1];
  }
  
  // Also check for patterns without dashes
  const altMatch = imageUrl.match(/optimized(XS|S|M|L)[\W_]/);
  if (altMatch) {
    return altMatch[1];
  }
  
  // Check URL path segments (avoid matching S in XS)
  const urlParts = imageUrl.split('/');
  for (const part of urlParts) {
    if (part.includes('optimized')) {
      const sizeMatch = part.match(/\b(XS|S|M|L)\b/);
      if (sizeMatch) {
        return sizeMatch[1];
      }
    }
  }
  
  return null;
}

// Comprehensive size details mapping
const getSizeDetails = (size: string) => {
  const sizeDetailsMap = {
    'XS': { 
      id: 'XS', 
      name: 'Extra Small', 
      width: 7, 
      height: 5, 
      frameWidth: 0.875, 
      frameHeight: 1.125, 
      price: 59.50, 
      description: '7" x 5" frame' 
    },
    'S': { 
      id: 'S', 
      name: 'Small', 
      width: 12, 
      height: 8, 
      frameWidth: 0.875, 
      frameHeight: 1.125, 
      price: 79.50, 
      description: '12" x 8" frame' 
    },
    'M': { 
      id: 'M', 
      name: 'Medium', 
      width: 17, 
      height: 11, 
      frameWidth: 0.875, 
      frameHeight: 1.125, 
      price: 119.50, 
      description: '17" x 11" frame' 
    },
    'L': { 
      id: 'L', 
      name: 'Large', 
      width: 24, 
      height: 18, 
      frameWidth: 0.875, 
      frameHeight: 1.125, 
      price: 149.50, 
      description: '24" x 18" frame' 
    }
  };
  
  return sizeDetailsMap[size as keyof typeof sizeDetailsMap];
};

// Get mat size based on selected frame size
const getMatSize = (size: string) => {
  const matSizeMap = {
    'S': '2.5"',
    'M': '3"', 
    'L': '3.5"'
  };
  return matSizeMap[size as keyof typeof matSizeMap] || '3"';
};

// Get total frame size based on selected frame size
const getTotalFrameSize = (size: string) => {
  const totalFrameSizeMap = {
    'XS': '12" × 10"',  // XS (displays as S to users)
    'S': '18" × 14"',   // S (displays as M to users)
    'M': '24" × 18"',   // M (displays as L to users)
    'L': '32" × 26"'    // L (displays as XL to users)
  };
  return totalFrameSizeMap[size as keyof typeof totalFrameSizeMap] || '24" × 18"';
};

const Success: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [frameConfig, setFrameConfig] = useState<FrameConfig | null>(null);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [showDebug, setShowDebug] = useState(false); // Hide debug by default
  const [debugState, setDebugState] = useState<DebugState | null>(null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailResent, setEmailResent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const { 
    selectedFrame, 
    selectedSize,
    selectedStyle,
    propertyImage,
    styleImages
  } = useFrame();

  // Debug state tracker
  useEffect(() => {
    const dependencyCheck = {
      sessionId: !!sessionId,
      frameConfig: !!frameConfig,
      order: !!order,
      orderSubmitted: orderSubmitted,
      orderSubmitting: orderSubmitting
    };

    setDebugState({
      sessionId,
      frameConfig,
      order,
      orderSubmitted,
      orderSubmitting,
      dependencyCheck,
      lastCheck: new Date().toISOString()
    });

    console.log('Debug State Update:', {
      sessionId: !!sessionId,
      frameConfig: !!frameConfig,
      order: !!order,
      orderSubmitted,
      orderSubmitting,
      shouldTriggerSubmission: !!(sessionId && frameConfig && order && !orderSubmitted && !orderSubmitting)
    });
  }, [sessionId, frameConfig, order, orderSubmitted, orderSubmitting]);

  // Extract session ID from URL, handling potential encoding issues
  useEffect(() => {
    // Get the raw URL to handle cases where the session_id parameter might be malformed
    const rawUrl = decodeURIComponent(window.location.href);
    
    // Try to extract session_id using regex to handle special characters
    const sessionMatch = rawUrl.match(/[?&]session_id[=≤]([^&]*)/i);
    const extractedSessionId = sessionMatch ? sessionMatch[1] : null;
    
    console.log('Raw URL:', rawUrl);
    console.log('Extracted session ID:', extractedSessionId);
    console.log('URL params session ID:', searchParams.get('session_id'));
    
    // Try multiple methods to extract the session ID
    const sessionId = extractedSessionId || 
                     searchParams.get('session_id') || 
                     new URLSearchParams(window.location.search).get('session_id');
                     
    console.log('Final session ID:', sessionId);
    setSessionId(sessionId);
  }, [searchParams]);

  // Load frame configuration from database first, then localStorage as fallback
  useEffect(() => {
    const loadFrameConfiguration = async () => {
      console.log('=== LOADING FRAME CONFIGURATION ===');
      
      // First try to load from database using checkout session ID
      if (sessionId) {
        try {
          console.log('Attempting to load frame config from database using session ID:', sessionId);
          
          // CRITICAL: Set the x-checkout-session header for RLS policies
          const { refreshClientSessionId } = await import('../lib/supabase');
          refreshClientSessionId();
          
          // Set the checkout session header for this specific query
          if (supabase?.rest) {
            supabase.rest.headers = {
              ...supabase.rest.headers,
              'x-checkout-session': sessionId
            };
          }
          
          const { data: propertySearchData, error } = await supabase
            .from('property_searches')
            .select(`
              lumaprints_order_id,
              selected_frame,
              selected_size,
              selected_style,
              frame_image_url,
              frame_price,
              shipping_method,
              shipping_cost,
              total_price,
              customer_first_name,
              customer_last_name,
              customer_email,
              address_line_1,
              address_line_2,
              city,
              state,
              zip_code,
              country
            `)
            .eq('stripe_checkout_session_id', sessionId)
            .maybeSingle();

          if (!error && propertySearchData && propertySearchData.selected_frame) {
            console.log('✅ Found frame config in database:', propertySearchData);
            
            // Convert database data to FrameConfig format
            const dbFrameConfig: FrameConfig = {
              selectedFrame: propertySearchData.selected_frame,
              lumaprintsOrderId: propertySearchData.lumaprints_order_id,
              selectedSize: propertySearchData.selected_size,
              selectedStyle: propertySearchData.selected_style,
              imageUrl: propertySearchData.frame_image_url || '',
              frameUrl: getFrameUrl(propertySearchData.selected_frame),
              frameName: getFrameName(propertySearchData.selected_frame),
              sizeDetails: getSizeDetails(propertySearchData.selected_size) || {
                id: propertySearchData.selected_size,
                name: propertySearchData.selected_size,
                width: 17,
                height: 11,
                frameWidth: 0.875,
                frameHeight: 1.125,
                price: propertySearchData.frame_price || 99.50,
                description: `${propertySearchData.selected_size} frame`
              },
              timestamp: Date.now()
            };
            
            console.log('✅ Loaded frame config from database successfully:', dbFrameConfig);
            setFrameConfig(dbFrameConfig);
            return; // Success, no need to check localStorage
          } else {
            console.log('❌ No frame config found in database or error:', error);
          }
        } catch (dbError) {
          console.error('❌ Error loading frame config from database:', dbError);
        }
      }

      // Fallback to localStorage
      try {
        console.log('Attempting to load frame config from localStorage...');
        const storedConfig = localStorage.getItem('frameConfig');
        console.log('Raw stored config:', storedConfig);
        
        if (storedConfig) {
          const config = JSON.parse(storedConfig) as FrameConfig;
          
          console.log('=== FRAME SIZE DETECTION DEBUG ===');
          console.log('Original config selectedSize:', config.selectedSize);
          console.log('Image URL:', config.imageUrl);
          
          // CRITICAL FIX: Extract correct size from optimized image URL if available
          const extractedSize = extractFrameSizeFromImageUrl(config.imageUrl);
          console.log('Extracted size from URL:', extractedSize);
          
          if (extractedSize && extractedSize !== config.selectedSize) {
            console.log(`🔧 SIZE MISMATCH DETECTED!`);
            console.log(`   Config says: ${config.selectedSize}`);
            console.log(`   Image URL suggests: ${extractedSize}`);
            console.log(`   Correcting selectedSize from ${config.selectedSize} to ${extractedSize}`);
            
            config.selectedSize = extractedSize;
            
            // Update the sizeDetails to match the extracted size
            const newSizeDetails = getSizeDetails(extractedSize);
            if (newSizeDetails) {
              config.sizeDetails = newSizeDetails;
              console.log(`✅ Updated sizeDetails for ${extractedSize}:`, newSizeDetails);
            }
          } else if (extractedSize) {
            console.log(`✅ Size is consistent: ${config.selectedSize} = ${extractedSize}`);
          } else {
            console.log(`⚠️  Could not extract size from URL, keeping original: ${config.selectedSize}`);
          }
          
          // Validate the final configuration
          console.log('=== FINAL FRAME CONFIG ===');
          console.log('Selected size:', config.selectedSize);
          console.log('Size details:', config.sizeDetails);
          console.log('Frame dimensions:', `${config.sizeDetails.width}" x ${config.sizeDetails.height}"`);
          
          // Ensure the configuration is valid for all sizes
          if (!['XS', 'S', 'M', 'L'].includes(config.selectedSize)) {
            console.error('Invalid frame size detected:', config.selectedSize);
            throw new Error(`Invalid frame size: ${config.selectedSize}`);
          }
          
          setFrameConfig(config);
          console.log('✅ Frame config loaded from localStorage and validated successfully');
          
          // Clean up localStorage after successful load
          localStorage.removeItem('frameConfig');
        } else {
          console.log('❌ No frame config found in localStorage');
          setError('Frame configuration not found');
        }
      } catch (error) {
        console.error('Error loading frame config from localStorage:', error);
        setError('Failed to load frame configuration');
      }
    };

    if (sessionId) {
      loadFrameConfiguration();
    }
  }, [sessionId]);

  // Helper function to get frame URL
  const getFrameUrl = (frameId: string) => {
    switch (frameId) {
      case 'gold':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Gold_M.png';
      case 'mocca':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Mocca_M.png';
      case 'natural':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Natural_M.png';
      case 'white':
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//White_M.png';
      case 'black':
      default:
        return 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Black_M.png';
    }
  };

  // Helper function to get frame name
  const getFrameName = (frameId: string) => {
    const frameNames = {
      'gold': 'Gold',
      'mocca': 'Espresso',
      'natural': 'Natural',
      'white': 'White',
      'black': 'Black'
    };
    return frameNames[frameId as keyof typeof frameNames] || frameId.charAt(0).toUpperCase() + frameId.slice(1);
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!sessionId) {
        if (sessionId === undefined) {
          setError('No session ID found');
          setLoading(false);
        }
        return;
      }

      try {
        console.log('Fetching order details for session ID:', sessionId);
        
        // Use our new edge function to get accurate order data
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-checkout-session?session_id=${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            ...getAdminModeHeaders(), // Include admin mode headers
          }
        });

        if (response.ok) {
          const result = await response.json();
          
          if (result.success && result.order) {
            console.log('Successfully fetched order data from', result.source + ':', result.order);
            
            // Convert to OrderData format
            const orderData: OrderData = {
              customer_id: result.order.customer_id,
              order_id: result.order.id || 0,
              checkout_session_id: result.order.checkout_session_id,
              payment_intent_id: result.order.payment_intent_id || sessionId,
              amount_subtotal: result.order.amount_subtotal || 0,
              amount_total: result.order.amount_total || 0,
              currency: result.order.currency || 'usd',
              payment_status: result.order.payment_status || 'paid',
              order_status: result.order.status || 'completed',
              order_date: result.order.created_at || new Date().toISOString()
            };
            
            setOrder(orderData);
            setLoading(false);
            
            // Track successful purchase and order confirmation
            trackPurchaseCompleted({
              orderId: result.order.id || sessionId,
              transactionId: result.order.payment_intent_id || sessionId,
              total: (result.order.amount_total || 0) / 100, // Convert from cents
              subtotal: (result.order.amount_subtotal || 0) / 100,
              currency: 'USD',
              style: frameConfig?.selectedStyle || 'watercolor',
              frame: frameConfig?.selectedFrame || 'black',
              size: frameConfig?.selectedSize || 'M',
              productId: `frame_${frameConfig?.selectedStyle}_${frameConfig?.selectedFrame}_${frameConfig?.selectedSize}`
            });
            
            trackOrderConfirmationView({
              orderId: result.order.id || sessionId,
              total: (result.order.amount_total || 0) / 100,
              currency: 'USD'
            });

            // Update widget for success page
            updateWidgetForSuccess(result.order.customer_email, result.order.id || sessionId);
            
            trackFunnelStep('Purchase Completed', 9, {
              order_value: (result.order.amount_total || 0) / 100,
              order_id: result.order.id || sessionId
            });
            
            trackFunnelStep('Order Confirmation Viewed', 10, {
              order_value: (result.order.amount_total || 0) / 100,
              order_id: result.order.id || sessionId
            });

            // Track purchase completion in Reddit
            trackRedditPurchase({
              total: (result.order.amount_total || 0) / 100,
              currency: 'USD',
              order_id: result.order.id || sessionId
            });

            // Track purchase completion in Facebook
            trackFacebookPurchase({
              total: (result.order.amount_total || 0) / 100,
              currency: 'USD',
              orderId: result.order.id || sessionId,
              productId: `frame_${frameConfig?.selectedSize || 'M'}_${frameConfig?.selectedFrame || 'black'}`
            }, result.order.customer_email);

            // Track purchase completion in Pinterest
            trackPinterestPurchase({
              total: (result.order.amount_total || 0) / 100,
              currency: 'USD',
              orderId: result.order.id || sessionId
            }, result.order.customer_email);
            
            return;
          }
        }

        console.log('Edge function failed, falling back to legacy order lookup...');
        
        // Fallback: check legacy getUserOrders for compatibility
        const orders = await getUserOrders();
        const matchingOrder = orders.find(order => order.checkout_session_id === sessionId);
        
        if (matchingOrder) {
          console.log('Found matching order in legacy system:', matchingOrder);
          setOrder(matchingOrder);
        } else {
          console.log('No matching order found, creating mock order for success page');
          
          // Create a mock order for the success page - the webhook will handle the real data
          const mockOrder: OrderData = {
            customer_id: 'stripe-customer',
            order_id: 0,
            checkout_session_id: sessionId,
            payment_intent_id: sessionId,
            amount_subtotal: 12900, // Will be updated when webhook processes
            amount_total: 12900, // Will be updated when webhook processes  
            currency: 'usd',
            payment_status: 'paid',
            order_status: 'pending',
            order_date: new Date().toISOString()
          };
          
          console.log('Created mock order for success page:', mockOrder);
          setOrder(mockOrder);
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        
        // Create fallback order even if there's an error
        const fallbackOrder: OrderData = {
          customer_id: 'customer',
          order_id: 0,
          checkout_session_id: sessionId,
          payment_intent_id: sessionId,
          amount_subtotal: 12900,
          amount_total: 12900,
          currency: 'usd',
          payment_status: 'paid',
          order_status: 'pending',
          order_date: new Date().toISOString()
        };
        
        console.log('Using fallback order due to fetch error:', fallbackOrder);
        setOrder(fallbackOrder);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [sessionId]);

  // Submit order to Lumaprints when we have all the necessary information
  useEffect(() => {
    const submitOrderToPrinter = async () => {
      console.log('Checking if should submit order to printer...', {
        sessionId: !!sessionId,
        frameConfig: !!frameConfig,
        order: !!order,
        orderSubmitted,
        orderSubmitting,
        shouldSubmit: !!(sessionId && frameConfig && order && !orderSubmitted && !orderSubmitting)
      });

      if (!sessionId || !frameConfig || orderSubmitted || orderSubmitting || !order) {
        console.log('Skipping order submission due to missing dependencies or already processed');
        return;
      }

      // ADDITIONAL PROTECTION: Check sessionStorage for already submitted orders
      const submittedOrdersKey = 'submitted_orders';
      const submittedOrders = JSON.parse(sessionStorage.getItem(submittedOrdersKey) || '[]');
      if (submittedOrders.includes(sessionId)) {
        console.log('🛑 Order already submitted (found in sessionStorage) - preventing duplicate');
        setOrderSubmitted(true);
        return;
      }

      console.log('=== ORDER SUBMISSION TO PRINTER ===');
      console.log('Frame config being used:', frameConfig);
      console.log('CRITICAL: Frame size being submitted:', frameConfig.selectedSize);
      console.log('CRITICAL: Frame dimensions:', `${frameConfig.sizeDetails.width}" x ${frameConfig.sizeDetails.height}"`);
      
      // Final validation before submission
      if (!['XS', 'S', 'M', 'L'].includes(frameConfig.selectedSize)) {
        console.error('Invalid frame size at submission:', frameConfig.selectedSize);
        setOrderError(`Invalid frame size: ${frameConfig.selectedSize}`);
        return;
      }
      
      setOrderSubmitting(true);

      try {
        // Look up property search using the checkout session ID
        let propertySearchId = null;
        try {
          console.log('=== PROPERTY SEARCH LOOKUP DEBUG ===');
          console.log('Looking up property search for session ID:', sessionId);
          console.log('Session ID type:', typeof sessionId);
          console.log('Session ID length:', sessionId?.length);
          
          // CRITICAL: Set the x-checkout-session header for RLS policies
          const { refreshClientSessionId } = await import('../lib/supabase');
          refreshClientSessionId();
          
          // Set the checkout session header for this specific query
          if (supabase?.rest) {
            supabase.rest.headers = {
              ...supabase.rest.headers,
              'x-checkout-session': sessionId
            };
          }
          
          console.log('Making Supabase query...');
          const { data: propertySearch, error: searchError } = await supabase
            .from('property_searches')
            .select('id, customer_first_name, customer_last_name, customer_email, address_line_1, address_line_2, city, state, zip_code, country, stripe_checkout_session_id, property_token')
            .eq('stripe_checkout_session_id', sessionId)
            .maybeSingle();

          console.log('=== QUERY RESULTS ===');
          console.log('Search error:', searchError);
          console.log('Property search found:', propertySearch);
          console.log('Property search count:', propertySearch ? 1 : 0);
          
          if (searchError && searchError.code !== 'PGRST116') {
            console.error('❌ Database error looking up property search:', searchError);
            throw new Error(`Database error: ${searchError.message}`);
          } else if (propertySearch) {
            propertySearchId = propertySearch.id;
            console.log('✅ Found property search with customer data:', {
              id: propertySearch.id,
              hasCustomerName: !!(propertySearch.customer_first_name && propertySearch.customer_last_name),
              hasCustomerEmail: !!propertySearch.customer_email,
              hasAddress: !!(propertySearch.address_line_1 && propertySearch.city && propertySearch.state && propertySearch.zip_code),
              sessionIdMatch: propertySearch.stripe_checkout_session_id === sessionId
            });

            // 🎯 TRACK PURCHASE COMPLETED EVENT
            console.log('🎯 Tracking purchase completed event...');
            try {
              // Get campaign info if this is a campaign property
              let campaignInfo = null;
              if (propertySearch.property_token) {
                campaignInfo = await campaignTracking.getCampaignPropertyInfo(propertySearch.property_token);
              }
              
              await campaignTracking.trackPurchaseCompleted({
                propertyId: propertySearch.id,
                campaignId: campaignInfo?.campaignId,
                accessCode: campaignInfo?.accessCode,
                checkoutSessionId: sessionId,
                paymentIntentId: order.payment_intent_id,
                orderAmount: order.amount_total
              });
              
              console.log('✅ Successfully tracked purchase completed event', {
                propertyId: propertySearch.id,
                campaignId: campaignInfo?.campaignId,
                checkoutSessionId: sessionId,
                orderAmount: order.amount_total,
                isCampaignProperty: !!campaignInfo?.campaignId
              });
            } catch (trackingError) {
              console.warn('⚠️ Failed to track purchase completed event:', trackingError);
              // Continue anyway - tracking shouldn't block the user experience
            }

            // Construct order data from property search and frame config
            const orderData = {
              frameTypeId: frameConfig?.selectedFrame || selectedFrame || 'black',
              frameSizeId: frameConfig?.selectedSize || selectedSize || 'M',
              imageUrl: frameConfig?.imageUrl || propertyImage || '',
              imageStyle: frameConfig?.selectedStyle || selectedStyle || 'watercolor',
              recipient: {
                firstName: propertySearch.customer_first_name,
                lastName: propertySearch.customer_last_name,
                addressLine1: propertySearch.address_line_1,
                addressLine2: propertySearch.address_line_2 || '',
                city: propertySearch.city,
                state: propertySearch.state,
                zipCode: propertySearch.zip_code,
                country: propertySearch.country || 'US'
              },
              billingDetails: {
                email: propertySearch.customer_email,
                firstName: propertySearch.customer_first_name,
                lastName: propertySearch.customer_last_name,
                addressLine1: propertySearch.address_line_1,
                addressLine2: propertySearch.address_line_2 || '',
                city: propertySearch.city,
                state: propertySearch.state,
                zipCode: propertySearch.zip_code,
                country: propertySearch.country || 'US'
              },
              propertyId: propertySearchId,
              checkoutSessionId: sessionId,
              paymentIntentId: order.payment_intent_id
            };
            
            // Validate that all required customer data is present
            const missingCustomerData = [];
            if (!orderData.recipient.firstName) missingCustomerData.push('first name');
            if (!orderData.recipient.lastName) missingCustomerData.push('last name');
            if (!orderData.billingDetails.email) missingCustomerData.push('email');
            if (!orderData.recipient.addressLine1) missingCustomerData.push('address line 1');
            if (!orderData.recipient.city) missingCustomerData.push('city');
            if (!orderData.recipient.state) missingCustomerData.push('state');
            if (!orderData.recipient.zipCode) missingCustomerData.push('zip code');
            
            if (missingCustomerData.length > 0) {
              console.error('❌ Missing required customer data:', missingCustomerData);
              setOrderError(`Missing customer information: ${missingCustomerData.join(', ')}. Please contact support.`);
              return;
            }
            
            console.log('=== FRAME CONTEXT VALUES DEBUG ===');
            console.log('selectedFrame:', selectedFrame);
            console.log('selectedSize:', selectedSize);
            console.log('selectedStyle:', selectedStyle);
            console.log('propertyImage:', propertyImage);
            console.log('=== FRAME CONFIG VALUES DEBUG ===');
            console.log('frameConfig?.selectedFrame:', frameConfig?.selectedFrame);
            console.log('frameConfig?.selectedSize:', frameConfig?.selectedSize);
            console.log('frameConfig?.selectedStyle:', frameConfig?.selectedStyle);
            console.log('frameConfig?.imageUrl:', frameConfig?.imageUrl);
            console.log('=== ORDER DATA DEBUG ===');
            console.log('frameTypeId:', orderData.frameTypeId);
            console.log('frameSizeId:', orderData.frameSizeId);
            console.log('imageUrl:', orderData.imageUrl);
            console.log('imageStyle:', orderData.imageStyle);
            console.log('recipient defined:', !!orderData.recipient);
            
            // Validate required fields before sending
            const missingFields = [];
            if (!orderData.frameTypeId) missingFields.push('frameTypeId');
            if (!orderData.frameSizeId) missingFields.push('frameSizeId');
            if (!orderData.imageUrl) missingFields.push('imageUrl');
            if (!orderData.imageStyle) missingFields.push('imageStyle');
            if (!orderData.recipient) missingFields.push('recipient');
            
            if (missingFields.length > 0) {
              console.error('❌ Missing required fields before sending:', missingFields);
              setOrderError(`Missing required fields: ${missingFields.join(', ')}`);
              return;
            }
            
            console.log('✅ All required fields present, proceeding with API call');
            console.log('=== SUBMITTING ORDER WITH CORRECTED SIZE ===');
            console.log('Frame size ID being sent:', orderData.frameSizeId);
            console.log('Expected dimensions for', orderData.frameSizeId + ':', 
              orderData.frameSizeId === 'S' ? '12" x 8"' :
              orderData.frameSizeId === 'M' ? '17" x 11"' :
              orderData.frameSizeId === 'L' ? '24" x 18"' : 'Unknown');
            console.log('Full order data:', orderData);

            // Call the place-order function
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/place-order`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                ...getAdminModeHeaders(), // Include admin mode headers
              },
              body: JSON.stringify(orderData)
            });

            const result = await response.json();
            
            // Store the complete API response for debugging
            setApiResponse({
              httpStatus: response.status,
              httpStatusText: response.statusText,
              responseData: result,
              requestData: orderData,
              timestamp: new Date().toISOString(),
              sessionId: sessionId
            });
            
            console.log('API Response:', result);
            
            if (response.ok && result.success) {
              console.log('✅ Order submitted successfully:', result);
              setOrderSubmitted(true);
              
              // Save to sessionStorage to prevent duplicates on page refresh
              const submittedOrdersKey = 'submitted_orders';
              const submittedOrders = JSON.parse(sessionStorage.getItem(submittedOrdersKey) || '[]');
              if (!submittedOrders.includes(sessionId)) {
                submittedOrders.push(sessionId);
                sessionStorage.setItem(submittedOrdersKey, JSON.stringify(submittedOrders));
                console.log('💾 Saved order submission to sessionStorage');
              }
            } else {
              console.error('❌ Order submission failed:', result);
              setOrderError(result.error || 'Failed to submit order to printer');
            }
          } else {
            console.error('❌ No property search found for checkout session:', sessionId);
            console.error('This means customer data was not saved properly during checkout');
            setOrderError('Customer information not found. Please contact support with your order confirmation.');
            return;
          }
        } catch (lookupError) {
          console.error('Error during property search lookup:', lookupError);
          setOrderError('Failed to process order');
          setApiResponse({
            error: 'Property search lookup failed',
            details: lookupError instanceof Error ? lookupError.message : 'Unknown error',
            sessionId: sessionId,
            timestamp: new Date().toISOString()
          });
        }
      } catch (submitError) {
        console.error('Error submitting order:', submitError);
        setOrderError('Failed to submit order to printer');
        setApiResponse({
          error: 'Order submission failed',
          details: submitError instanceof Error ? submitError.message : 'Unknown error',
          sessionId: sessionId,
          timestamp: new Date().toISOString()
        });
      } finally {
        setOrderSubmitting(false);
      }
    };

    // Trigger immediately, no delay
    console.log('Setting up order submission effect...');
    submitOrderToPrinter();
  }, [sessionId, frameConfig, order, orderSubmitted, orderSubmitting]);

  // Function to resend order confirmation email
  const resendOrderConfirmation = async () => {
    if (!order || !frameConfig) {
      setEmailError('Order details not available');
      return;
    }

    setResendingEmail(true);
    setEmailError(null);
    setEmailResent(false);

    try {
      // Get the customer email from the property search or use a fallback
      let customerEmail = null;
      
      // Try to get email from property search
      if (sessionId) {
        const { data: propertySearch } = await supabase
          .from('property_searches')
          .select('customer_email')
          .eq('stripe_checkout_session_id', sessionId)
          .single();
        
        customerEmail = propertySearch?.customer_email;
      }

      if (!customerEmail) {
        throw new Error('Customer email not found. Please contact support for assistance.');
      }

      // Prepare order details for email
      const orderDetails = {
        // Order identification
        lumaprintsOrderId: apiResponse?.responseData?.lumaprints_order_id || frameConfig?.lumaprintsOrderId || 'TBD',
        checkoutSessionId: sessionId,
        
        // Frame configuration
        frameType: frameConfig.selectedFrame,
        frameSize: frameConfig.selectedSize,
        frameTypeDisplay: getFrameName(frameConfig.selectedFrame) + ' Wood',
        frameSizeDisplay: frameConfig.selectedSize === 'S' ? 'Small (18" x 14")' : 
                         frameConfig.selectedSize === 'M' ? 'Medium (24" x 18")' : 
                         frameConfig.selectedSize === 'L' ? 'Large (32" x 26")' : frameConfig.selectedSize,
        dimensions: `${frameConfig.sizeDetails.width}" x ${frameConfig.sizeDetails.height}"`,
        
        // Customer information
        customerName: `Customer`, // Default since we don't have specific name
        customerFirstName: 'Customer',
        customerLastName: '',
        customerEmail: customerEmail,
        
        // Product details
        imageUrl: frameConfig.imageUrl,
        imageStyle: frameConfig.selectedStyle,
        frameUrl: frameConfig.frameUrl,
        
        // Pricing information
        totalCost: order.amount_total,
        amount_total: order.amount_total,
        amount_subtotal: order.amount_subtotal,
        
        // Order status and timing
        orderStatus: 'processing',
        orderDate: order.order_date,
        orderDateFormatted: order.order_date ? new Date(order.order_date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long', 
          day: 'numeric'
        }) : new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long', 
          day: 'numeric'
        }),
        
        // Additional metadata
        sandbox: true,
        resent: true,
        timestamp: Date.now()
      };

      console.log('Resending order confirmation to:', customerEmail);
      console.log('Order details:', orderDetails);

      // Call the send-order-webhook function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-order-webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          recipientEmail: customerEmail,
          orderDetails: orderDetails
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        console.log('✅ Order confirmation resent successfully:', result);
        setEmailResent(true);
        // Clear the success message after 5 seconds
        setTimeout(() => setEmailResent(false), 5000);
      } else {
        console.error('❌ Failed to resend order confirmation:', result);
        throw new Error(result.error || 'Failed to send email');
      }

    } catch (error) {
      console.error('Error resending order confirmation:', error);
      setEmailError(error instanceof Error ? error.message : 'Failed to resend email');
      // Clear the error message after 10 seconds
      setTimeout(() => setEmailError(null), 10000);
    } finally {
      setResendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#F5ECE1' }}>
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          <img 
            src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
            alt="Framemyhome"
            className="h-12 mx-auto mb-6"
          />
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Processing your order...</p>
          <p className="text-sm text-gray-500 mt-4">This may take a few moments</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#F5ECE1' }}>
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          <img 
            src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
            alt="Framemyhome"
            className="h-12 mx-auto mb-6"
          />
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">!</span>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Something went wrong
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Home className="w-5 h-5" />
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#F5ECE1' }}>
      <div className="max-w-md w-full">
        {/* Main Success Card */}
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <img 
              src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png"
              alt="Framemyhome"
              className="h-12 mx-auto mb-6"
            />
            
            {/* Frame Preview or Success Icon */}
            {frameConfig ? (
              <div className="mb-6">
                <div className="relative mx-auto mb-4" style={{ width: '168px', height: '138px' }}>
                  {/* Selected Image Behind Frame */}
                  <div className="absolute inset-[20%] overflow-hidden">
                    <img
                      src={frameConfig.imageUrl}
                      alt={`${frameConfig.selectedStyle} style`}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                  </div>
                  {/* Frame Overlay */}
                  <img
                    src={frameConfig.frameUrl}
                    alt={`${frameConfig.selectedFrame} frame`}
                    className="relative w-full h-full object-contain"
                  />
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {getTotalFrameSize(frameConfig.selectedSize)} {getFrameName(frameConfig.selectedFrame)} wood framed {frameConfig.selectedStyle} style print
                </p>
              </div>
            ) : (
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
            )}

            {/* Success Message */}
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-gray-600 mb-6">
              Thank you for your purchase. Your order has been confirmed and will be processed shortly.
            </p>

            {/* Order Details */}
            {order && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2 text-base">
                  <Package className="w-5 h-5" />
                  Order Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order ID:</span>
                    <span className="text-gray-900">
                      {orderSubmitting ? (
                        'Processing...'
                      ) : apiResponse?.responseData?.lumaprints_order_id ? (
                        apiResponse.responseData.lumaprints_order_id
                      ) : frameConfig?.lumaprintsOrderId ? (
                        frameConfig.lumaprintsOrderId
                      ) : order.order_id === 0 ? (
                        'provided via email'
                      ) : (
                        `#${order.order_id}`
                      )}
                    </span>
                  </div>
                  {/* Temporarily hidden - price display
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium text-gray-900">
                      ${((order.amount_total || 0) / 100).toFixed(2)} {order.currency?.toUpperCase() || 'USD'}
                    </span>
                  </div>
                  */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {order.payment_status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="text-gray-900">
                      {order.order_date ? new Date(order.order_date).toLocaleDateString() : 'Processing'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div className="text-left mb-6">
              <h3 className="font-medium text-gray-900 mb-2 text-base">What happens next?</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• You'll receive an email confirmation and receipt shortly</li>
                <li>• Your framed print will be prepared and shipped</li>
                <li>• Tracking information will be sent to your email</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <Link
                to="/"
                className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Return to Home
              </Link>
              
              {/* Resend Order Confirmation Button */}
              <button
                onClick={resendOrderConfirmation}
                disabled={resendingEmail}
                className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                  resendingEmail 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : emailResent
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {resendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : emailResent ? (
                  <>
                    <Check className="w-4 h-4" />
                    Email Sent!
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Resend Order Confirmation
                  </>
                )}
              </button>
              
              {/* Email Status Messages */}
              {emailError && (
                <div className="text-sm text-red-600 text-center p-2 bg-red-50 rounded-lg">
                  {emailError}
                </div>
              )}
            </div>

        </div>
      </div>
    </div>
  );
};

export default Success;